import { useEffect, useState } from "react";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import { getCustomerProfile, type CustomerProfile } from "../lib/auth";

type CustomerProfilePageProps = {
  customerId: string;
  navigate: (route: string) => void;
};

export function CustomerProfilePage({
  customerId,
  navigate,
}: CustomerProfilePageProps) {
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!customerId) {
      return;
    }
    let isActive = true;
    setIsLoading(true);
    setError(null);
    getCustomerProfile(customerId)
      .then((data) => {
        if (isActive) {
          setProfile(data);
        }
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load customer profile.";
        setError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [customerId]);

  const customerName =
    profile?.customer.name ?? profile?.customer.email ?? "Customer profile";

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/inbox")}>
              Inbox
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Customer profile</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Customer Profile"
        title={customerName}
        description={
          profile?.customer.email
            ? `Primary contact: ${profile.customer.email}`
            : "Aggregate history across conversations and work items."
        }
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/inbox")}>
              Back to inbox
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        {isLoading ? (
          <div className="space-y-4">
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent className="space-y-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={`customer-event-${index}`} className="h-4 w-full" />
                ))}
              </CardContent>
            </Card>
          </div>
        ) : error ? (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        ) : profile ? (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-border bg-card/60">
                <CardHeader>
                  <CardTitle className="text-sm text-muted-foreground">
                    Recent conversations
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold text-foreground">
                  {profile.recentThreads.length}
                </CardContent>
              </Card>
              <Card className="border-border bg-card/60">
                <CardHeader>
                  <CardTitle className="text-sm text-muted-foreground">
                    Open work items
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold text-foreground">
                  {profile.openWorkItems.length}
                </CardContent>
              </Card>
              <Card className="border-border bg-card/60">
                <CardHeader>
                  <CardTitle className="text-sm text-muted-foreground">
                    Summary entries
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold text-foreground">
                  {profile.summaries.length}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="border-border bg-card/60">
                <CardHeader>
                  <CardTitle className="text-lg">Recent conversations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-foreground/90">
                  {profile.recentThreads.length === 0 ? (
                    <Empty className="border-border/60 bg-muted/40">
                      <EmptyHeader>
                        <EmptyTitle>No conversations yet</EmptyTitle>
                        <EmptyDescription className="text-muted-foreground">
                          This customer has no recent threads.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    profile.recentThreads.map((thread) => (
                      <button
                        key={thread.id}
                        type="button"
                        className="w-full rounded-lg border border-border bg-muted/60 p-3 text-left hover:border-ring/60"
                        onClick={() => navigate(`/threads/${thread.id}`)}
                      >
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{thread.status}</span>
                          <span>{new Date(thread.updatedAt).toLocaleString()}</span>
                        </div>
                        <p className="mt-2 text-sm text-foreground">
                          {thread.subject ?? "Untitled conversation"}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {thread.summary}
                        </p>
                      </button>
                    ))
                  )}
                </CardContent>
              </Card>

              <Card className="border-border bg-card/60">
                <CardHeader>
                  <CardTitle className="text-lg">Open work items</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-foreground/90">
                  {profile.openWorkItems.length === 0 ? (
                    <Empty className="border-border/60 bg-muted/40">
                      <EmptyHeader>
                        <EmptyTitle>No open work items</EmptyTitle>
                        <EmptyDescription className="text-muted-foreground">
                          Approved items will appear here once assigned.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    profile.openWorkItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="w-full rounded-lg border border-border bg-muted/60 p-3 text-left hover:border-ring/60"
                        onClick={() => navigate(`/entities/${item.id}`)}
                      >
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{item.status}</span>
                          <span>{item.priority}</span>
                        </div>
                        <p className="mt-2 text-sm text-foreground">{item.title}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Updated {new Date(item.updatedAt).toLocaleString()}
                        </p>
                      </button>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Historical summaries</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-foreground/90">
                {profile.summaries.length === 0 ? (
                  <Empty className="border-border/60 bg-muted/40">
                    <EmptyHeader>
                      <EmptyTitle>No summaries captured yet</EmptyTitle>
                      <EmptyDescription className="text-muted-foreground">
                        Summaries will appear as insights are recorded.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  profile.summaries.map((entry) => (
                    <div
                      key={entry.threadId}
                      className="rounded-lg border border-border bg-muted/60 p-3"
                    >
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.updatedAt).toLocaleString()}
                      </p>
                      <p className="text-sm text-foreground">{entry.summary}</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </section>
    </PageShell>
  );
}
